import { cloneDeep } from "lodash";
import { v4 as uuid } from "uuid";

import { ReturnSchema } from "../../../../constants";
import {
  APIFiltersOption,
  APISortByOption,
  BaseFunctionName,
  DataSourceNode,
  DescribeColumn,
  FunctionAttribute,
  FunctionAuthorizationFlowInput,
  FunctionNode,
  FunctionNodeBasic,
  FunctionParameterInput,
  InputParameter,
  Metadata,
  ParameterType
} from "../../../../types";
import { assertNever } from "../../../util/assertNever";
import { IdentifierMap, parseIdentifiersFromJavascriptProgram } from "../../parseUtils";
import { PipelineStepType } from "../forms/pipeline/constants";
import {
  DataSourceNodeWithFunctions,
  GeneralActionTypes,
  SetFieldValueAction
} from "../forms/types";
import { FunctionEditorMetadata } from "../FunctionEditor";
import {
  BaseCode,
  BaseFunctionParameterMapping,
  EditorTab,
  PreviewResult
} from "../index";
import { isSQLLike, SupportedIntegration } from "../support";

import { parseIdentifiers } from "./parsers";
import {
  ConditionActionType,
  Conditional,
  EditorFunctionNode,
  PipelineParameterInput,
  PipelineStep,
  ConditionParts
} from "./queries";
import {
  defaultBaseFunction,
  defaultBaseFunctionParameterMapping,
  defaultMetadata,
  defaultMetadataReducer,
  defaultPipelineStepTitle,
  defaultReturnSchema,
  filterOutInputParametersBoundToPrefix,
  getParameters,
  getReturnSchemaForPipelineStep,
  keyPipelineParameter,
  pipelineParametersToMetadata,
  pipelineParametersToParameters,
  pipelineStepsToParameters,
  pipelineStepsToSlugs,
  preservePipelineStepsCachedData,
  stepNameToSlug,
  toFunctionState,
  updateStepField
} from "./util";

export interface FunctionState<M> {
  integration: SupportedIntegration | undefined;
  title: string;
  baseFunctionId: string;
  baseFunctionName: BaseFunctionName | "";
  baseFunctionParameterMapping: BaseFunctionParameterMapping;
  functionId: string;
  metadata: M;
  reducer: string;
  metadataReducer: string;
  parameters: FunctionParameterInput[];
  attributes: FunctionAttribute[];
  returnSchema: ReturnSchema | undefined;
  authorizationFlows: FunctionAuthorizationFlowInput[];
  environmentIdsWithCredentials: { [id: string]: true };
  pipelineSteps?: PipelineStep[];
  pipelineParameters?: PipelineParameterInput[];
}

export interface UIState<M> {
  activeEditorTab: EditorTab;
  lastSavedFunctionState: FunctionState<M> | undefined;

  // lastSeenParameters is the last viewed set of parameters
  // that the user has seen in the INPUT tab.
  lastSeenParameters: FunctionParameterInput[];

  lastSeenAttributes: FunctionAttribute[];

  // parameterCache is a cache of parameters that has
  // information about changes the user has made to a parsed
  // parameter.  It is used to populate parameters with
  // previously configured information (e.g. required and
  // type info) when a parameter has gone away and then
  // reappeared.
  parameterCache: FunctionParameterInput[];

  previewAttributes: FunctionAttribute[];
  previewId: number;
  previewReturnSchema: ReturnSchema | undefined;
  refreshId: number;
  selectedPipelineStepIndex?: number;
  selectedPipelineStepConditionIndex?: number;
}

export type State<M = Metadata> = FunctionState<M> & UIState<M>;

export const getEmptyState = <M>(metadata: M): State<M> => ({
  integration: undefined,
  title: "",
  baseFunctionId: "",
  baseFunctionName: "",
  baseFunctionParameterMapping: {},
  functionId: "",
  metadata,
  reducer: "data",
  metadataReducer: "metadata",
  attributes: [],
  parameters: [],
  returnSchema: undefined,
  authorizationFlows: [],
  environmentIdsWithCredentials: {},
  pipelineSteps: undefined,
  pipelineParameters: undefined,
  ...getEmptyUIState()
});

const getEmptyUIState = <M>(): UIState<M> => ({
  activeEditorTab: EditorTab.CONFIGURE,
  lastSavedFunctionState: undefined,
  lastSeenParameters: [],
  lastSeenAttributes: [],
  parameterCache: [],
  previewAttributes: [],
  previewId: 0,
  previewReturnSchema: undefined,
  refreshId: 0
});

export enum ActionType {
  CHANGE_DATA_SOURCE = "CHANGE_DATA_SOURCE",
  LOAD_FUNCTION = "LOAD_FUNCTION",
  REFRESH_ATTRIBUTES = "REFRESH_ATTRIBUTES",
  UPDATE_ACTIVE_EDITOR_TAB = "UPDATE_ACTIVE_EDITOR_TAB",
  UPDATE_ATTRIBUTES = "UPDATE_ATTRIBUTES",
  UPDATE_BASE_CONFIG = "UPDATE_BASE_CONFIG",
  UPDATE_BASE_FUNCTION = "UPDATE_BASE_FUNCTION",
  UPDATE_PARAMETER = "UPDATE_PARAMETER",
  UPDATE_PIPELINE_PARAMETER = "UPDATE_PIPELINE_PARAMETER",
  UPDATE_PREVIEW_RESULT = "UPDATE_PREVIEW_RESULT",
  UPDATE_RETURN_SCHEMA = "UPDATE_RETURN_SCHEMA",
  UPDATE_FUNCTION_AUTHORIZATION_FLOW = "UPDATE_FUNCTION_AUTHORIZATION_FLOW",
  UPDATE_DESCRIBE_COLUMNS = "UPDATE_DESCRIBE_COLUMNS",
  UPDATE_REDUCER = "UPDATE_REDUCER",
  ADD_PIPELINE_STEP = "ADD_PIPELINE_STEP",
  ADD_LOADED_PIPELINE_STEP = "ADD_LOADED_PIPELINE_STEP",
  REMOVE_PIPELINE_STEP = "REMOVE_PIPELINE_STEP",
  SELECT_PIPELINE_STEP = "SELECT_PIPELINE_STEP",
  SET_PIPELINE_STEP_NAME = "SET_PIPELINE_STEP_NAME",
  SET_PIPELINE_STEP_TYPE = "SET_PIPELINE_STEP_TYPE",
  SET_PIPELINE_STEP_DATA_SOURCE = "SET_PIPELINE_STEP_DATA_SOURCE",
  SET_PIPELINE_STEP_FUNCTION = "SET_PIPELINE_STEP_FUNCTION",
  SET_PIPELINE_STEP_ITERATOR_BINDING = "SET_PIPELINE_STEP_ITERATOR_BINDING",
  SET_PIPELINE_STEP_ITERATOR_ITEM_NAME = "SET_PIPELINE_STEP_LOOP_ITERATOR_ITEM_NAME",
  SET_PIPELINE_STEP_EXPRESSION = "SET_PIPELINE_STEP_EXPRESSION",
  SET_PIPELINE_STEP_CONDITION = "SET_PIPELINE_STEP_CONDITION",
  SET_PIPELINE_STEP_CONDITION_FUNCTION = "SET_PIPELINE_STEP_CONDITION_FUNCTION",
  SET_PIPELINE_STEP_CONDITION_ACTION_TYPE = "SET_PIPELINE_STEP_CONDITION_ACTION_TYPE",
  SET_PIPELINE_STEP_INPUT_PARAMETERS = "SET_PIPELINE_STEP_INPUT_PARAMETERS",
  SET_PIPELINE_STEP_CONDITION_INPUT_PARAMETERS = "SET_PIPELINE_STEP_CONDITION_INPUT_PARAMETERS",
  SET_PIPELINE_STEP_LOOP_FUNCTION = "SET_PIPELINE_STEP_LOOP_FUNCTION",
  SET_PIPELINE_STEP_LOOP_INPUT_PARAMETERS = "SET_PIPELINE_STEP_LOOP_INPUT_PARAMETERS",
  SELECT_PIPELINE_STEP_CONDITION = "SELECT_PIPELINE_STEP_CONDITION",
  LOAD_PIPELINE_STEP_CONDITION_FUNCTION = "LOAD_PIPELINE_STEP_CONDITION_FUNCTION"
}

export interface LoadFunctionAction {
  type: ActionType.LOAD_FUNCTION;
  payload: EditorFunctionNode<any>;
}

export interface ChangeDataSourceAction {
  type: ActionType.CHANGE_DATA_SOURCE;
  payload: DataSourceNodeWithFunctions<FunctionNodeBasic>;
}

export interface BaseFunctionConfig<C> {
  baseFunctionParameterMapping: BaseFunctionParameterMapping;
  code?: C;
}

export interface UpdateActiveEditorTabAction {
  type: ActionType.UPDATE_ACTIVE_EDITOR_TAB;
  payload: {
    tab: EditorTab;
  };
}

export interface UpdateAttributesAction {
  type: ActionType.UPDATE_ATTRIBUTES;
  payload: FunctionAttribute[];
}

export interface RefreshAttributesAction {
  type: ActionType.REFRESH_ATTRIBUTES;
}

export interface UpdateBaseConfigAction<C> {
  type: ActionType.UPDATE_BASE_CONFIG;
  payload: BaseFunctionConfig<C>;
}

export interface UpdateBaseFunctionAction {
  type: ActionType.UPDATE_BASE_FUNCTION;
  payload: {
    baseFunctionId: string;
    baseFunctionName: BaseFunctionName;
  };
}

export interface UpdateParameterAction {
  type: ActionType.UPDATE_PARAMETER;
  payload: FunctionParameterInput;
}

export interface UpdatePipelineParameterAction {
  type: ActionType.UPDATE_PIPELINE_PARAMETER;
  payload: PipelineParameterInput;
}

export interface UpdatePreviewResult {
  type: ActionType.UPDATE_PREVIEW_RESULT;
  payload: PreviewResult;
}

export interface UpdateReturnSchema {
  type: ActionType.UPDATE_RETURN_SCHEMA;
  payload: ReturnSchema;
}

export interface UpdateAuthorizationFlowAction {
  type: ActionType.UPDATE_FUNCTION_AUTHORIZATION_FLOW;
  payload: { environmentId: string; authorizationFlowId: string | undefined };
}

export interface UpdateDescribeColumns {
  type: ActionType.UPDATE_DESCRIBE_COLUMNS;
  payload: DescribeColumn[];
}

export interface UpdateReducer {
  type: ActionType.UPDATE_REDUCER;
  payload: string;
}

export interface AddPipelineStepAction {
  type: ActionType.ADD_PIPELINE_STEP;
}

export interface AddLoadedPipelineStepAction {
  type: ActionType.ADD_LOADED_PIPELINE_STEP;
  payload: {
    function: FunctionNode;
    dataSource: DataSourceNode;
  };
}

export interface RemovePipelineStepAction {
  type: ActionType.REMOVE_PIPELINE_STEP;
  payload: {
    stepIndex: number;
  };
}

export interface SelectPipelineStepAction {
  type: ActionType.SELECT_PIPELINE_STEP;
  payload: {
    stepIndex: number;
  };
}

export interface SetPipelineStepNameAction {
  type: ActionType.SET_PIPELINE_STEP_NAME;
  payload: {
    stepIndex: number;
    name: string;
  };
}

export interface SetPipelineStepTypeAction {
  type: ActionType.SET_PIPELINE_STEP_TYPE;
  payload: {
    stepIndex: number;
    stepType: PipelineStepType;
  };
}

export interface SetPipelineStepDataSourceAction {
  type: ActionType.SET_PIPELINE_STEP_DATA_SOURCE;
  payload: {
    stepIndex: number;
    dataSource: DataSourceNode;
  };
}

export interface SetPipelineStepFunctionAction {
  type: ActionType.SET_PIPELINE_STEP_FUNCTION;
  payload: {
    stepIndex: number;
    function: FunctionNode;
  };
}

export interface SetPipelineStepExpressionAction {
  type: ActionType.SET_PIPELINE_STEP_EXPRESSION;
  payload: {
    stepIndex: number;
    expression: string;
  };
}

export interface SetPipelineStepConditionAction {
  type: ActionType.SET_PIPELINE_STEP_CONDITION;
  payload: {
    stepIndex: number;
    condition?: string;
    parts?: ConditionParts[];
    conjunctions?: string[];
  };
}

export interface SetPipelineStepConditionTypeAction {
  type: ActionType.SET_PIPELINE_STEP_CONDITION_ACTION_TYPE;
  payload: {
    stepIndex: number;
    conditionIndex: number;
    actionType: ConditionActionType;
  };
}

export interface SetPipelineStepConditionFunctionAction {
  type: ActionType.SET_PIPELINE_STEP_CONDITION_FUNCTION;
  payload: {
    stepIndex: number;
    conditionIndex: number;
    functionId: string | undefined; // undefined to clear out the function
  };
}
export interface SetPipelineStepInputParametersAction {
  type: ActionType.SET_PIPELINE_STEP_INPUT_PARAMETERS;
  payload: {
    stepIndex: number;
    inputParameters: InputParameter[];
  };
}

export interface SelectPipelineStepCondition {
  type: ActionType.SELECT_PIPELINE_STEP_CONDITION;
  payload: {
    conditionIndex: number;
  };
}

export interface SetPipelineStepConditionInputParametersAction {
  type: ActionType.SET_PIPELINE_STEP_CONDITION_INPUT_PARAMETERS;
  payload: {
    stepIndex: number;
    conditionIndex: number;
    inputParameters: InputParameter[];
  };
}

export interface LoadPipelineStepConditionFunctionAction {
  type: ActionType.LOAD_PIPELINE_STEP_CONDITION_FUNCTION;
  payload: {
    stepIndex: number;
    conditionIndex: number;
    function: FunctionNode;
  };
}

export interface SetPipelineStepIteratorBindingAction {
  type: ActionType.SET_PIPELINE_STEP_ITERATOR_BINDING;
  payload: {
    stepIndex: number;
    iteratorBinding: string;
  };
}

export interface SetPipelineStepIteratorItemNameAction {
  type: ActionType.SET_PIPELINE_STEP_ITERATOR_ITEM_NAME;
  payload: {
    stepIndex: number;
    iteratorItemName: string;
  };
}

type Action<C> =
  | LoadFunctionAction
  | SetFieldValueAction<keyof State>
  | ChangeDataSourceAction
  | UpdateActiveEditorTabAction
  | UpdateAttributesAction
  | RefreshAttributesAction
  | UpdateBaseConfigAction<C>
  | UpdateBaseFunctionAction
  | UpdateParameterAction
  | UpdatePipelineParameterAction
  | UpdatePreviewResult
  | UpdateReturnSchema
  | UpdateAuthorizationFlowAction
  | UpdateDescribeColumns
  | UpdateReducer
  | AddPipelineStepAction
  | AddLoadedPipelineStepAction
  | RemovePipelineStepAction
  | SelectPipelineStepAction
  | SetPipelineStepNameAction
  | SetPipelineStepTypeAction
  | SetPipelineStepDataSourceAction
  | SetPipelineStepFunctionAction
  | SetPipelineStepExpressionAction
  | SetPipelineStepConditionAction
  | SetPipelineStepConditionFunctionAction
  | SetPipelineStepConditionTypeAction
  | SetPipelineStepInputParametersAction
  | SelectPipelineStepCondition
  | SetPipelineStepConditionInputParametersAction
  | LoadPipelineStepConditionFunctionAction
  | SetPipelineStepIteratorBindingAction
  | SetPipelineStepIteratorItemNameAction;

function handleUpdateAttributes<M>(
  state: State<M>,
  attributes: FunctionAttribute[]
): State<M> {
  const newState: State<M> = { ...state, attributes };
  if (state.activeEditorTab === EditorTab.OUTPUTS) {
    newState.lastSeenAttributes = newState.attributes;
  }
  return newState;
}

function handleUpdateReturnSchema<C, M extends Metadata<C>>(
  state: State<M>,
  returnSchema: ReturnSchema | undefined
): State<M> {
  const categories = new Set(state.metadata.categories);
  categories.delete("returns:many");
  categories.delete("returns:one");
  switch (returnSchema) {
    case ReturnSchema.OBJECT_ARRAY:
      categories.add("returns:many");
      break;
    case ReturnSchema.OBJECT:
      categories.add("returns:one");
      break;
  }
  return {
    ...state,
    returnSchema,
    metadata: {
      ...state.metadata,
      categories: Array.from(categories).sort()
    }
  };
}

export const reducer = <
  C = BaseCode,
  M extends FunctionEditorMetadata<C> = FunctionEditorMetadata<C>
>(
  state: State<M>,
  action: Action<C>
): State<Metadata<any>> => {
  switch (action.type) {
    case ActionType.CHANGE_DATA_SOURCE: {
      const fn = defaultBaseFunction(action.payload);
      const mapping = fn
        ? defaultBaseFunctionParameterMapping(fn.name as BaseFunctionName)
        : {};

      const metadataReducer = fn
        ? defaultMetadataReducer(fn.name as BaseFunctionName)
        : "metadata";
      const returnSchema = fn
        ? defaultReturnSchema(fn.name as BaseFunctionName)
        : undefined;
      return {
        ...getEmptyState(fn ? defaultMetadata(fn.name as BaseFunctionName) : {}),
        integration: action.payload.integration,
        title: state.title,
        baseFunctionId: fn?.id || "",
        baseFunctionName: (fn?.name as BaseFunctionName) || "",
        baseFunctionParameterMapping: mapping,
        metadataReducer,
        returnSchema
      };
    }
    case ActionType.LOAD_FUNCTION: {
      const s = toFunctionState(action.payload);

      const pipelineSteps = s.pipelineSteps
        ? preservePipelineStepsCachedData(state.pipelineSteps || [], s.pipelineSteps!)
        : undefined;

      return {
        ...getEmptyState(s.baseFunctionName ? defaultMetadata(s.baseFunctionName) : {}),
        ...s,
        activeEditorTab: state.activeEditorTab || EditorTab.CONFIGURE,
        lastSavedFunctionState: cloneDeep(s),
        lastSeenParameters: s.parameters,
        lastSeenAttributes: s.attributes,
        parameterCache: s.parameters,
        previewAttributes: s.attributes,
        previewReturnSchema: s.returnSchema,
        pipelineSteps: pipelineSteps,
        selectedPipelineStepIndex: state.selectedPipelineStepIndex,
        selectedPipelineStepConditionIndex: state.selectedPipelineStepConditionIndex,
        pipelineParameters: s.pipelineParameters
      };
    }
    case ActionType.UPDATE_ACTIVE_EDITOR_TAB: {
      const newState: State<M> = {
        ...state,
        activeEditorTab: action.payload.tab
      };
      if (action.payload.tab === EditorTab.INPUTS) {
        newState.lastSeenParameters = newState.parameters;
      }
      if (action.payload.tab === EditorTab.OUTPUTS) {
        newState.lastSeenAttributes = newState.attributes;
      }
      return newState;
    }
    case ActionType.UPDATE_ATTRIBUTES: {
      return handleUpdateAttributes(state, action.payload);
    }
    case ActionType.REFRESH_ATTRIBUTES: {
      const newState: State<M> = {
        ...state,
        refreshId: state.previewId,
        returnSchema: state.previewReturnSchema,
        attributes: [...state.previewAttributes]
      };
      if (state.activeEditorTab === EditorTab.OUTPUTS) {
        newState.lastSeenAttributes = newState.attributes;
      }
      return newState;
    }
    case ActionType.UPDATE_BASE_CONFIG:
      if (!state.baseFunctionName) {
        console.warn("useFunctionEditor: baseFunctionName is not set", action.payload);
        return state;
      }
      if (!state.integration) {
        console.warn("useFunctionEditor: integration is not set", action.payload);
        return state;
      }

      const { baseFunctionParameterMapping, code } = action.payload;
      const metadata = { ...state.metadata };
      if (code !== undefined) {
        metadata.code = code;
      }

      const [identifiers] = parseIdentifiers(
        state.integration,
        state.baseFunctionName,
        baseFunctionParameterMapping,
        metadata
      );

      const parameters = getParameters(state.parameterCache, identifiers);

      return {
        ...state,
        metadata,
        baseFunctionParameterMapping,
        parameters
      };
    case ActionType.UPDATE_BASE_FUNCTION: {
      if (!state.integration) {
        console.warn("useFunctionEditor: integration is not set", action.payload);
        return state;
      }

      const { baseFunctionId, baseFunctionName } = action.payload;
      const metadata = defaultMetadata(baseFunctionName);
      const metadataReducer = defaultMetadataReducer(baseFunctionName);
      const baseFunctionParameterMapping =
        defaultBaseFunctionParameterMapping(baseFunctionName);
      const returnSchema = defaultReturnSchema(baseFunctionName);

      const [identifiers] = parseIdentifiers(
        state.integration,
        baseFunctionName,
        baseFunctionParameterMapping,
        metadata
      );
      const parameters = getParameters(state.parameters, identifiers);

      return {
        ...state,
        baseFunctionId,
        baseFunctionName,
        baseFunctionParameterMapping,
        returnSchema,
        metadata,
        metadataReducer,
        parameters
      };
    }
    case ActionType.UPDATE_PARAMETER: {
      const updated = action.payload;
      const parameters = [...state.parameters];
      const idx = parameters.findIndex(p => p.name === updated.name);
      if (idx > -1) {
        parameters[idx] = updated;
      }

      const lastSeenParameters =
        state.activeEditorTab === EditorTab.INPUTS
          ? parameters
          : state.lastSeenParameters;
      return {
        ...state,
        parameters,
        parameterCache: parameters,
        lastSeenParameters
      };
    }
    case ActionType.UPDATE_PIPELINE_PARAMETER: {
      const newPipelineParameters = (state.pipelineParameters || []).map(p => {
        if (p.id === action.payload.id) {
          return {
            ...p,
            ...action.payload
          };
        }

        return p;
      });

      const newParameters = pipelineParametersToParameters(newPipelineParameters);
      const key = keyPipelineParameter(action.payload);

      return {
        ...state,
        pipelineParameters: newPipelineParameters,
        parameters: newParameters,
        parameterCache: newParameters,
        metadata: {
          ...state.metadata,
          parameterMapping: {
            ...state.metadata.parameterMapping,
            [key]: action.payload.pipelineName
          }
        }
      };
    }
    case GeneralActionTypes.SET_FIELD_VALUE:
      const { fieldName, fieldValue } = action.payload;

      // Some request body types have special default metadata reducers
      if (
        fieldName === "metadata" &&
        fieldValue.request_type !== state.metadata.request_type &&
        state.baseFunctionName
      ) {
        const currentMetadataReducer = defaultMetadataReducer(
          state.baseFunctionName,
          state.metadata.request_type
        );
        const metadataReducer = defaultMetadataReducer(
          state.baseFunctionName,
          fieldValue.request_type
        );

        // Only set the default metadata reducer if metadata reducer has not been modified
        if (
          state.metadataReducer === currentMetadataReducer &&
          state.metadataReducer !== metadataReducer
        ) {
          return {
            ...state,
            [action.payload.fieldName]: action.payload.fieldValue,
            metadataReducer
          };
        }
      }

      return {
        ...state,
        [action.payload.fieldName]: action.payload.fieldValue
      };
    case ActionType.UPDATE_PREVIEW_RESULT: {
      return {
        ...state,
        previewId: state.previewId + 1,
        previewAttributes: action.payload.attributes,
        previewReturnSchema: action.payload.returnSchema
      };
    }
    case ActionType.UPDATE_RETURN_SCHEMA: {
      return handleUpdateReturnSchema(state, action.payload);
    }
    case ActionType.UPDATE_FUNCTION_AUTHORIZATION_FLOW:
      const { authorizationFlowId, environmentId } = action.payload;
      const flows = [...state.authorizationFlows];
      const idx = flows.findIndex(f => f.environmentId === environmentId);
      const replace = authorizationFlowId
        ? [
            {
              environmentId,
              authorizationFlowId
            }
          ]
        : [];
      flows.splice(idx > -1 ? idx : flows.length, 1, ...replace);
      flows.sort((a, b) => a.environmentId.localeCompare(b.environmentId));
      return {
        ...state,
        authorizationFlows: flows
      };
    case ActionType.UPDATE_DESCRIBE_COLUMNS: {
      const { payload: columns } = action;

      const attributes = columns.map<FunctionAttribute>(c => {
        const existingAttribute = state.attributes.find(a => a.name === c.sourceName);
        return {
          name: c.sourceName,
          type: c.sourceType,
          key: existingAttribute?.key || c.sourceKey || false
        };
      });

      const nextState = handleUpdateAttributes(state, attributes);

      const filtersOptions = columns.reduce<APIFiltersOption[]>(
        (acc, c) =>
          c.operators.length
            ? acc.concat([
                {
                  operators: c.operators,
                  sourceName: c.sourceName,
                  sourceType: c.sourceType
                }
              ])
            : acc,
        []
      );

      const sortByOptions = columns.reduce<APISortByOption[]>(
        (acc, c) => (c.orderable ? acc.concat([c.sourceName]) : acc),
        []
      );

      const metadata = {
        ...nextState.metadata,
        filters: { options: filtersOptions },
        sortBy: { options: sortByOptions }
      };

      return { ...nextState, metadata };
    }
    case ActionType.UPDATE_REDUCER: {
      let returnSchema = state.returnSchema;
      if (isSQLLike(state.integration)) {
        switch (action.payload) {
          case "data":
            returnSchema = ReturnSchema.OBJECT_ARRAY;
            break;
          case "data[0]":
            returnSchema = ReturnSchema.OBJECT;
            break;
        }
      }
      return handleUpdateReturnSchema(
        { ...state, reducer: action.payload },
        returnSchema
      );
    }
    case ActionType.ADD_PIPELINE_STEP: {
      const newSteps = (state.pipelineSteps || []).concat({
        title: "",
        slug: "",
        order: state.pipelineSteps?.length || 0,
        stopOnError: false,
        returnsData: false,
        inputParameters: []
      });

      return {
        ...state,
        attributes: [],
        returnSchema: undefined,
        pipelineSteps: newSteps,
        // Select the step
        selectedPipelineStepIndex: newSteps.length - 1
      };
    }
    case ActionType.ADD_LOADED_PIPELINE_STEP: {
      const parameterToStepSlug = new Map<string, string>();
      for (const pipelineParameter of state.pipelineParameters || []) {
        parameterToStepSlug.set(
          pipelineParameter.pipelineName,
          pipelineParameter.stepSlug
        );
      }

      const existingSlugs = (state.pipelineSteps || []).map(s => s.slug);

      const newStep: PipelineStep = {
        id: uuid(),
        type: PipelineStepType.FUNCTION,
        stopOnError: false,
        returnsData: false,
        order: state.pipelineSteps?.length || 0,
        title: action.payload.function.title || "",
        slug: stepNameToSlug(action.payload.function.title || "", existingSlugs),
        function: action.payload.function,
        dataSource: action.payload.dataSource,
        inputParameters: (action.payload.function.functionParameters?.edges || [])
          .filter(e => e.node.required)
          .map(e => ({
            name: e.node.name,
            required: e.node.required,
            type: ParameterType.UNRESOLVED
          }))
      };

      const newPipelineSteps = (state.pipelineSteps || []).concat(newStep);
      const newPipelineParameters = pipelineStepsToParameters(
        newPipelineSteps,
        state.metadata.parameterMapping || {}
      );

      return {
        ...state,
        integration: SupportedIntegration.PIPELINES,
        attributes: (action.payload.function.functionAttributes?.edges || []).map(
          e => ({
            name: e.node.sourceName,
            type: e.node.sourceType,
            key: e.node.sourceKey
          })
        ),
        returnSchema: action.payload.function.returnSchema,
        pipelineSteps: newPipelineSteps,
        pipelineParameters: newPipelineParameters,
        parameters: pipelineParametersToParameters(newPipelineParameters),
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(newPipelineParameters)
        }
      };
    }
    case ActionType.REMOVE_PIPELINE_STEP: {
      const stepIndex = action.payload.stepIndex;
      const step = state.pipelineSteps?.[stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      const pipelineParameters = state.pipelineParameters?.filter(
        p => p.stepNumber !== step?.order
      );

      const newSteps = state.pipelineSteps
        ?.filter((_, index) => index !== stepIndex)
        .map((s, stepIndex) => {
          const wasLoopBoundToStep = s.iteratorBinding === step.slug;
          return {
            ...s,
            order: stepIndex,
            iteratorBinding: wasLoopBoundToStep ? undefined : s.iteratorBinding,
            inputParameters: !wasLoopBoundToStep
              ? s.inputParameters
              : s.inputParameters.filter(ip =>
                  filterOutInputParametersBoundToPrefix(s.iteratorItemName, ip)
                )
          };
        });

      const lastStep = newSteps?.length ? newSteps[newSteps?.length - 1] : undefined;

      return {
        ...state,
        attributes: !lastStep
          ? []
          : (lastStep.function?.functionAttributes?.edges || []).map(e => ({
              name: e.node.sourceName,
              key: e.node.sourceKey,
              type: e.node.sourceType
            })),
        returnSchema: getReturnSchemaForPipelineStep(lastStep),
        pipelineSteps: newSteps,
        pipelineParameters: pipelineParameters,
        parameters: (pipelineParameters || []).map(p => ({
          name: p.pipelineName,
          type: p.type,
          required: p.required
        })),
        // If selected step is removed, clear selected step
        selectedPipelineStepIndex:
          state.selectedPipelineStepIndex === stepIndex
            ? undefined
            : state.selectedPipelineStepIndex,
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(pipelineParameters)
        }
      };
    }
    case ActionType.SELECT_PIPELINE_STEP: {
      return {
        ...state,
        selectedPipelineStepIndex: action.payload.stepIndex,
        selectedPipelineStepConditionIndex: undefined
      };
    }
    case ActionType.SET_PIPELINE_STEP_NAME: {
      const stepIndex = action.payload.stepIndex;
      const step = state.pipelineSteps?.[stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      const oldSlug = step.slug;
      const existingSlugs = pipelineStepsToSlugs(state.pipelineSteps, stepIndex);
      const newSlug = stepNameToSlug(action.payload.name, existingSlugs);
      const newPipelineParameters = state.pipelineParameters?.map(p => ({
        ...p,
        stepSlug: p.stepNumber === step.order ? newSlug : p.stepSlug
      }));

      let newPipelineSteps = (state.pipelineSteps || []).slice();
      newPipelineSteps[stepIndex] = {
        ...step,
        title: action.payload.name,
        slug: newSlug
      };

      // Update input parameter bindings to the old slug
      newPipelineSteps = newPipelineSteps.map((step, index) => {
        // New step should not have any input parameter references to itself
        if (index === stepIndex) {
          return step;
        }

        return {
          ...step,
          inputParameters: step.inputParameters.map(ip => {
            if (ip.type !== ParameterType.BINDING) {
              return ip;
            }

            return {
              ...ip,
              binding: ip.binding === oldSlug ? newSlug : ip.binding
            };
          }),
          conditions: step.conditions?.map(condition => ({
            ...condition,
            inputParameters: condition.inputParameters.map(ip => {
              if (ip.type !== ParameterType.BINDING) {
                return ip;
              }

              return {
                ...ip,
                binding: ip.binding === oldSlug ? newSlug : ip.binding
              };
            })
          })),
          iteratorBinding:
            step.iteratorBinding === oldSlug ? newSlug : step.iteratorBinding
        };
      });

      return {
        ...state,
        pipelineSteps: newPipelineSteps,
        pipelineParameters: newPipelineParameters,
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(newPipelineParameters)
        }
      };
    }
    case ActionType.SET_PIPELINE_STEP_TYPE: {
      const stepIndex = action.payload.stepIndex;
      const step = state.pipelineSteps?.[stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      const isLastStep = state.pipelineSteps!.length - 1 === action.payload.stepIndex;

      const parameterToStepSlug = new Map<string, string>();
      for (const pipelineParameter of state.pipelineParameters || []) {
        parameterToStepSlug.set(
          pipelineParameter.pipelineName,
          pipelineParameter.stepSlug
        );
      }
      const newPipelineParameters = state.pipelineParameters?.filter(
        p => p.stepNumber !== step.order
      );

      const existingSlugs = pipelineStepsToSlugs(state.pipelineSteps, stepIndex);
      const stepTitle = defaultPipelineStepTitle(step.order, action.payload.stepType);
      const stepSlug = stepNameToSlug(stepTitle, existingSlugs);
      const conditions: Conditional[] | undefined =
        action.payload.stepType === PipelineStepType.CONDITIONAL
          ? [
              {
                condition: "",
                parts: [
                  {
                    lhs: "",
                    operator: "",
                    rhs: ""
                  }
                ],
                actionType: ConditionActionType.PENDING,
                inputParameters: []
              },
              {
                parts: [],
                actionType: ConditionActionType.PENDING,
                inputParameters: []
              }
            ]
          : undefined;

      return {
        ...state,
        pipelineSteps: (state.pipelineSteps || []).map((s, index) => {
          if (index === action.payload.stepIndex) {
            return {
              ...s,
              title: stepTitle,
              slug: stepSlug,
              type: action.payload.stepType,
              expression: undefined,
              dataSource: undefined,
              function: undefined,
              iteratorBinding: undefined,
              iteratorItemName: undefined,
              conditions
            };
          }

          const wasLoopBoundToStep = s.iteratorBinding === step.slug;
          return {
            ...s,
            iteratorBinding: wasLoopBoundToStep ? undefined : s.iteratorBinding,
            inputParameters: !wasLoopBoundToStep
              ? s.inputParameters
              : s.inputParameters.filter(ip =>
                  filterOutInputParametersBoundToPrefix(s.iteratorItemName, ip)
                )
          };
        }),
        pipelineParameters: newPipelineParameters,
        parameters: state.parameters.filter(p => {
          return parameterToStepSlug.get(p.name) !== step.slug;
        }),
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(newPipelineParameters)
        },
        returnSchema: isLastStep ? ReturnSchema.UNKNOWN : state.returnSchema
      };
    }
    case ActionType.SET_PIPELINE_STEP_DATA_SOURCE: {
      const step = state.pipelineSteps?.[action.payload.stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      const parameterToStepSlug = new Map<string, string>();
      for (const pipelineParameter of state.pipelineParameters || []) {
        parameterToStepSlug.set(
          pipelineParameter.pipelineName,
          pipelineParameter.stepSlug
        );
      }
      const newPipelineParameters = state.pipelineParameters?.filter(
        p => p.stepNumber !== step.order
      );

      return {
        ...state,
        pipelineSteps: (state.pipelineSteps || []).map((s, index) => {
          if (index === action.payload.stepIndex) {
            return {
              ...s,
              dataSource: action.payload.dataSource,
              function: undefined
            };
          }

          const wasLoopBoundToStep = s.iteratorBinding === step.slug;
          return {
            ...s,
            iteratorBinding: wasLoopBoundToStep ? undefined : s.iteratorBinding,
            inputParameters: !wasLoopBoundToStep
              ? s.inputParameters
              : s.inputParameters.filter(ip =>
                  filterOutInputParametersBoundToPrefix(s.iteratorItemName, ip)
                )
          };
        }),
        pipelineParameters: newPipelineParameters,
        parameters: state.parameters.filter(p => {
          return parameterToStepSlug.get(p.name) !== step.slug;
        }),
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(newPipelineParameters)
        }
      };
    }
    case ActionType.SET_PIPELINE_STEP_FUNCTION: {
      const stepIndex = action.payload.stepIndex;

      const step = state.pipelineSteps?.[stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      const isLastStep = state.pipelineSteps!.length - 1 === stepIndex;

      const func = action.payload.function;

      const parameterToStepSlug = new Map<string, string>();
      for (const pipelineParameter of state.pipelineParameters || []) {
        parameterToStepSlug.set(
          pipelineParameter.pipelineName,
          pipelineParameter.stepSlug
        );
      }

      const existingSlugs = pipelineStepsToSlugs(state.pipelineSteps, stepIndex);
      const newPipelineSteps: PipelineStep[] = (state.pipelineSteps || []).map(
        (s, index) => {
          if (index === action.payload.stepIndex) {
            const wasNameSet = s.title !== "" && s.title !== s.function?.title;
            const newName = wasNameSet ? s.title : action.payload.function.title || "";

            return {
              ...s,
              title: newName,
              slug: stepNameToSlug(newName, existingSlugs),
              function: action.payload.function,
              inputParameters: (action.payload.function.functionParameters?.edges || [])
                .filter(fp => fp.node.required)
                .map(e => ({
                  name: e.node.name,
                  required: e.node.required,
                  type: ParameterType.UNRESOLVED
                }))
            };
          } else {
            const wasLoopBoundToStep = s.iteratorBinding === step.slug;
            return {
              ...s,
              iteratorBinding: wasLoopBoundToStep ? undefined : s.iteratorBinding,
              inputParameters: !wasLoopBoundToStep
                ? s.inputParameters
                : s.inputParameters.filter(ip =>
                    filterOutInputParametersBoundToPrefix(s.iteratorItemName, ip)
                  )
            };
          }
        }
      );

      const newPipelineParameters = pipelineStepsToParameters(
        newPipelineSteps,
        state.metadata.parameterMapping || {}
      );

      return {
        ...state,
        attributes: !isLastStep
          ? state.attributes
          : (func.functionAttributes?.edges || []).map(e => ({
              name: e.node.sourceName,
              type: e.node.sourceType,
              key: e.node.sourceKey
            })),
        returnSchema: !isLastStep ? state.returnSchema : func.returnSchema,
        pipelineSteps: newPipelineSteps,
        pipelineParameters: newPipelineParameters,
        parameters: pipelineParametersToParameters(newPipelineParameters),
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(newPipelineParameters)
        }
      };
    }
    case ActionType.SET_PIPELINE_STEP_EXPRESSION: {
      const step = state.pipelineSteps?.[action.payload.stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      const isLastStep = state.pipelineSteps?.length === action.payload.stepIndex + 1;

      let identifiers: IdentifierMap = {};

      try {
        identifiers = parseIdentifiersFromJavascriptProgram(action.payload.expression);
      } catch {
        // If we fail to get identifiers, we stop, since the user has an error
        // and we want to keep the original identifiers
        return {
          ...state
        };
      }

      const existingInputParametersMap = new Map<string, InputParameter>(
        step.inputParameters.map(ip => [ip.name, ip])
      );

      const newPipelineSteps = updateStepField(
        state.pipelineSteps || [],
        action.payload.stepIndex,
        step => ({
          ...step,
          expression: action.payload.expression,
          inputParameters: Object.values(identifiers).map(identifier => {
            if (existingInputParametersMap.has(identifier.name)) {
              return existingInputParametersMap.get(identifier.name)!;
            }

            return {
              name: identifier.name,
              required: true,
              type: ParameterType.UNRESOLVED
            };
          })
        })
      );

      const newPipelineParameters = pipelineStepsToParameters(
        newPipelineSteps,
        state.metadata.parameterMapping || {}
      );

      return {
        ...state,
        pipelineSteps: newPipelineSteps,
        pipelineParameters: newPipelineParameters,
        parameters: pipelineParametersToParameters(newPipelineParameters),
        attributes: [],
        returnSchema: !isLastStep
          ? state.returnSchema
          : getReturnSchemaForPipelineStep(newPipelineSteps[action.payload.stepIndex]),
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(newPipelineParameters)
        }
      };
    }
    case ActionType.SET_PIPELINE_STEP_CONDITION: {
      const step = state.pipelineSteps?.[action.payload.stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      if (step.conditions?.length !== 2) {
        throw new Error("Conditions not set");
      }

      const existingInputParametersMap = new Map<string, InputParameter>(
        step.inputParameters.map(ip => [ip.name, ip])
      );

      let identifiers: IdentifierMap = {};
      try {
        for (const condition of action.payload.parts || []) {
          const newIdentifiers = parseIdentifiersFromJavascriptProgram(condition.rhs);
          identifiers = {
            ...identifiers,
            ...newIdentifiers
          };
        }

        if (action.payload.condition?.length) {
          identifiers = {
            ...identifiers,
            ...parseIdentifiersFromJavascriptProgram(action.payload.condition)
          };
        }
      } catch {
        // If we fail to get identifiers, we stop, since the user has an error
        // and we want to keep the original identifiers
        return {
          ...state
        };
      }

      const newPipelineSteps = updateStepField(
        state.pipelineSteps || [],
        action.payload.stepIndex,
        step => ({
          ...step,
          inputParameters: Object.values(identifiers).map(identifier => {
            if (existingInputParametersMap.has(identifier.name)) {
              return existingInputParametersMap.get(identifier.name)!;
            }

            return {
              name: identifier.name,
              required: true,
              type: ParameterType.UNRESOLVED
            };
          }),
          conditions: step.conditions!.map((c, index) => {
            if (index === 0) {
              return {
                ...c,
                condition: action.payload.condition,
                parts: action.payload.parts,
                conjunctions: action.payload.conjunctions
              };
            }

            return c;
          })
        })
      );

      const newPipelineParameters = pipelineStepsToParameters(
        newPipelineSteps,
        state.metadata.parameterMapping || {}
      );

      return {
        ...state,
        pipelineSteps: newPipelineSteps,
        pipelineParameters: newPipelineParameters,
        parameters: pipelineParametersToParameters(newPipelineParameters),
        attributes: [],
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(newPipelineParameters)
        }
      };
    }
    case ActionType.SET_PIPELINE_STEP_CONDITION_ACTION_TYPE: {
      const step = state.pipelineSteps?.[action.payload.stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      const newPipelineSteps = updateStepField(
        state.pipelineSteps || [],
        action.payload.stepIndex,
        step => ({
          ...step,
          conditions: step.conditions!.map((c, index) => {
            if (index === action.payload.conditionIndex) {
              return {
                ...c,
                actionType: action.payload.actionType,
                functionId: undefined,
                function: undefined
              };
            }

            return c;
          })
        })
      );

      const newPipelineParameters = pipelineStepsToParameters(
        newPipelineSteps,
        state.metadata.parameterMapping || {}
      );

      return {
        ...state,
        pipelineSteps: newPipelineSteps,
        pipelineParameters: newPipelineParameters,
        parameters: pipelineParametersToParameters(newPipelineParameters),
        attributes: [],
        returnSchema: ReturnSchema.UNKNOWN,
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(newPipelineParameters)
        }
      };
    }
    case ActionType.SET_PIPELINE_STEP_CONDITION_FUNCTION: {
      const step = state.pipelineSteps?.[action.payload.stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      if (step.conditions?.length !== 2) {
        throw new Error("Conditions not set");
      }

      const newConditions: Conditional[] = step.conditions!.map((c, index) => {
        if (index === action.payload.conditionIndex) {
          return {
            ...c,
            functionId: action.payload.functionId,
            function: undefined,
            inputParameters: [] // Reset inputParameters on a function change
          };
        }

        return c;
      });

      const newPipelineSteps = updateStepField(
        state.pipelineSteps || [],
        action.payload.stepIndex,
        step => ({
          ...step,
          conditions: newConditions
        })
      );

      const newPipelineParameters = pipelineStepsToParameters(
        newPipelineSteps,
        state.metadata.parameterMapping || {}
      );

      return {
        ...state,
        pipelineSteps: newPipelineSteps,
        pipelineParameters: newPipelineParameters,
        parameters: pipelineParametersToParameters(newPipelineParameters),
        attributes: [],
        returnSchema: ReturnSchema.UNKNOWN,
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(newPipelineParameters)
        }
      };
    }
    case ActionType.SET_PIPELINE_STEP_INPUT_PARAMETERS: {
      const pipelineSteps = updateStepField(
        state.pipelineSteps || [],
        action.payload.stepIndex,
        step => ({
          ...step,
          inputParameters: action.payload.inputParameters
        })
      );

      const pipelineParameters = pipelineStepsToParameters(
        pipelineSteps,
        state.metadata.parameterMapping || {}
      );

      return {
        ...state,
        pipelineSteps,
        pipelineParameters,
        parameters: pipelineParametersToParameters(pipelineParameters),
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(pipelineParameters)
        }
      };
    }
    case ActionType.SELECT_PIPELINE_STEP_CONDITION: {
      return {
        ...state,
        selectedPipelineStepConditionIndex: action.payload.conditionIndex
      };
    }
    case ActionType.SET_PIPELINE_STEP_CONDITION_INPUT_PARAMETERS: {
      const step = state.pipelineSteps?.[action.payload.stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      step.conditions = !step.conditions
        ? undefined
        : step.conditions.map((condition, index) => {
            if (index === action.payload.conditionIndex) {
              return {
                ...condition,
                inputParameters: action.payload.inputParameters
              };
            }

            return condition;
          });

      const pipelineSteps = state.pipelineSteps || [];

      const pipelineParameters = pipelineStepsToParameters(
        pipelineSteps,
        state.metadata.parameterMapping || {}
      );

      return {
        ...state,
        pipelineSteps,
        pipelineParameters,
        parameters: pipelineParametersToParameters(pipelineParameters),
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(pipelineParameters)
        }
      };
    }
    case ActionType.LOAD_PIPELINE_STEP_CONDITION_FUNCTION: {
      const step = state.pipelineSteps?.[action.payload.stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      step.conditions = !step.conditions
        ? undefined
        : step.conditions.map((condition, index) => {
            if (index === action.payload.conditionIndex) {
              return {
                ...condition,
                function: action.payload.function,
                inputParameters: condition.inputParameters.length
                  ? condition.inputParameters
                  : (action.payload.function.functionParameters?.edges || [])
                      .filter(e => e.node.required)
                      .map(e => ({
                        name: e.node.name,
                        required: e.node.required,
                        type: ParameterType.UNRESOLVED
                      }))
              };
            }

            return condition;
          });

      const pipelineSteps = state.pipelineSteps || [];

      const pipelineParameters = pipelineStepsToParameters(
        pipelineSteps,
        state.metadata.parameterMapping || {}
      );

      return {
        ...state,
        pipelineSteps,
        pipelineParameters,
        parameters: pipelineParametersToParameters(pipelineParameters),
        metadata: {
          ...state.metadata,
          parameterMapping: pipelineParametersToMetadata(pipelineParameters)
        }
      };
    }
    case ActionType.SET_PIPELINE_STEP_ITERATOR_BINDING: {
      const step = state.pipelineSteps?.[action.payload.stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      const newPipelineSteps = (state.pipelineSteps || []).slice();
      newPipelineSteps[action.payload.stepIndex] = {
        ...step,
        iteratorBinding: action.payload.iteratorBinding,
        iteratorItemName: step.iteratorItemName ? step.iteratorItemName : "currentItem"
      };

      return {
        ...state,
        pipelineSteps: newPipelineSteps
      };
    }
    case ActionType.SET_PIPELINE_STEP_ITERATOR_ITEM_NAME: {
      const step = state.pipelineSteps?.[action.payload.stepIndex];
      if (!step) {
        throw new Error("Step not found");
      }

      const newPipelineSteps = (state.pipelineSteps || []).slice();
      newPipelineSteps[action.payload.stepIndex] = {
        ...step,
        iteratorItemName: action.payload.iteratorItemName
      };

      return {
        ...state,
        pipelineSteps: newPipelineSteps
      };
    }
    default:
      return assertNever(action);
  }
};
